import {
  ChartWrapper,
  ColumnChart,
  HorizontalBarChart,
  type HorizontalBarProps,
  LineChart,
  type LinechartProps,
} from "components/charts";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { noBarData, noData, validLineData } from "../../../Common/utils/performanceUtils";
import { type RequestState } from "features/Application/globaltypes/fetchRequest";
import "../flowPerformance.scss";
import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { useCallback, useMemo, useState } from "react";
import { type ColumnDataPoint, type Props as ColumnChartProps } from "components/charts/types/ColumnChart";
import { SERVER_DATE_ONLY_FORMAT } from "utils/dateTimeUtils";
import moment from "moment";
import { sharedGoogleReportingBarProps } from "features/Reporting/Google/utils/shared";
import { orgChartTooltipFormatter } from "../../state/selectors/flowPerformanceGoalSelectors";
import { reportingColors } from "features/Library/Common/utils/constants";

type CopilotUsageChartsProps = {
  header: string;
  goalLineChart: LinechartProps;
  goalLineState: RequestState<any>;
  goalEngagement: HorizontalBarProps;
  goalTotalsState: RequestState<any>;
};

const ACTIVE_USERS_TITLES = ["Active Users - Column Chart", "Active Users - Line Chart"];
const bar = "bar";
const line = "line";
export type ChartState = typeof line | typeof bar;

const sharedColumnChartProps: Partial<ColumnChartProps> = {
  margins: sharedGoogleReportingBarProps.margins,
  transitionDurationInMs: sharedGoogleReportingBarProps.transitionDurationInMs,
  yTickValueType: sharedGoogleReportingBarProps.yTickValueType,
};

const CopilotUsageCharts = ({
  header,
  goalLineChart,
  goalLineState,
  goalTotalsState,
  goalEngagement,
}: CopilotUsageChartsProps) => {
  const [activeUsersChartState, setActiveUsersChartState] = useState<ChartState>(bar);

  const handleActiveUsersDropdownChange = useCallback(
    (dropdownValue: string) =>
      dropdownValue === ACTIVE_USERS_TITLES[0] ? setActiveUsersChartState(bar) : setActiveUsersChartState(line),
    [],
  );

  const columnData: ColumnDataPoint[] = goalLineChart.xData[0]?.map((x: string, idx: number) => {
    return {
      id: `${idx}`,
      category: moment(x).format(SERVER_DATE_ONLY_FORMAT),
      value: goalLineChart.yData[0][idx],
      fillColor: reportingColors.blue,
    };
  });

  const columnTooltipFormatter = useCallback(
    (dataPoint: ColumnDataPoint) => {
      const index = columnData.findIndex((entry) => entry.id === dataPoint.id);
      const orgAvg = goalLineChart.dashedYData?.[0][index];
      return orgChartTooltipFormatter(dataPoint.value, orgAvg);
    },
    [columnData, goalLineChart.dashedYData],
  );

  const activeUsersChart = useMemo(() => {
    if (goalLineChart.yData.length === 0) {
      return null;
    }

    return activeUsersChartState === line ? (
      <LineChart
        {...sharedAccountReportingLineProps}
        {...goalLineChart}
        tooltipFormatter={(_xValue, flowAvg, _index, _xIndex, OrgAvg) => {
          return orgChartTooltipFormatter(flowAvg, OrgAvg);
        }}
      />
    ) : (
      <ColumnChart
        {...sharedColumnChartProps}
        data={columnData}
        domain={[0, Math.max(...columnData?.map((d) => d.value))]}
        xAxisTickFormat={(d) => {
          const dateToFormat = moment(d.toString(), SERVER_DATE_ONLY_FORMAT).local(true);
          return dateToFormat.format("MMM DD").replace(",", "");
        }}
        tooltipFormatter={columnTooltipFormatter}
      />
    );
  }, [activeUsersChartState, goalLineChart, columnData, columnTooltipFormatter]);

  const hasActiveUsersData = validLineData([goalLineChart.yData[0]]);

  return (
    <div className="flowPerformanceApplication">
      <h2>{header}</h2>
      <div className="copilotChartsSection">
        <div className="barChart">
          <ChartWrapper
            titles={ACTIVE_USERS_TITLES}
            onChange={handleActiveUsersDropdownChange}
            showLegend={activeUsersChartState === line}
            legendLabels={activeUsersChartState === line ? ["Flow"] : []}
            colorRange={[reportingColors.blue]}
          >
            <RequestStatusRenderer state={goalLineState}>
              {hasActiveUsersData ? activeUsersChart : noData(null)}
            </RequestStatusRenderer>
          </ChartWrapper>
        </div>
        <div className="horizontalBarChart">
          <ChartWrapper titles={["Usage by Application - Bar Chart"]}>
            <RequestStatusRenderer state={goalTotalsState}>
              {noBarData(...(goalEngagement?.data?.map((d) => d.value) || [])) ? (
                noData(null)
              ) : (
                <HorizontalBarChart
                  {...sharedAccountReportingHorizontalBarProps}
                  data={goalEngagement?.data ?? []}
                  xTickValueType={undefined}
                  xAxisTickFormat={(d) => `${Number(d)}%`}
                  tooltipFormatter={goalEngagement?.tooltipFormatter}
                />
              )}
            </RequestStatusRenderer>
          </ChartWrapper>
        </div>
      </div>
    </div>
  );
};

export default CopilotUsageCharts;
